import { artUrl, comingSoonText, isComingSoon, KIND_LABEL, label, type Game } from "./catalogue.ts";
import { h, link } from "./dom.ts";
import { downloadPath, gameActionLink, launch, playLink, type GameAction } from "./play.ts";
import { navigate } from "./router.ts";

/** Background for a game: its cover art over the palette gradient (art can be missing). */
export function coverStyle(g: Game, variant: "full" | "thumb" | "blur" = "thumb"): Partial<CSSStyleDeclaration> {
  const art = artUrl(g.art, variant);
  const gradient = `linear-gradient(160deg, ${safeColour(g.palette.top)}, ${safeColour(g.palette.bottom)})`;
  return { backgroundImage: art ? `url("${art}"), ${gradient}` : gradient };
}

function safeColour(c: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(c) ? c : "#202027";
}

export function gameCard(g: Game): HTMLElement {
  return h(
    "li",
    { class: "card" },
    link(
      `/g/${g.id}`,
      { class: "card-link", "aria-label": `${g.title} by ${g.creator.name}` },
      h("div", { class: "card-art", style: coverStyle(g) }, g.art ? null : h("span", { class: "card-art-title", "aria-hidden": "true" }, g.title)),
      h(
        "div",
        { class: "card-meta" },
        h("span", { class: "card-title" }, g.title),
        h("span", { class: "card-sub" }, `${label(g.genre)} · ${g.creator.name}`),
        isComingSoon(g) ? h("span", { class: "card-soon" }, h("span", { class: "badge warn" }, comingSoonText(g))) : null,
      ),
    ),
  );
}

export function gameGrid(games: Game[], empty = "Nothing here yet."): HTMLElement {
  if (games.length === 0) return h("p", { class: "dim" }, empty);
  return h("ul", { class: "grid" }, games.map(gameCard));
}

export function chips(g: Game): HTMLElement {
  return h(
    "ul",
    { class: "chips", "aria-label": "Tags" },
    h("li", null, KIND_LABEL[g.kind]?.replace(/s$/, "") ?? g.kind),
    h("li", null, label(g.genre)),
    g.facets.multiplayer ? h("li", null, "Multiplayer") : null,
    g.facets.input.map((i) => h("li", null, label(i))),
  );
}

/**
 * A launcher action on a game ("Notify me in Banger", "Tip the creator"): a real thrdm://game/ link, upgraded to fall
 * back to the download page like Play.
 */
export function actionButton(g: Pick<Game, "id" | "title">, action: GameAction, text: string, cls = "btn ghost"): HTMLElement {
  const href = gameActionLink(g.id, action);
  return h(
    "a",
    {
      class: cls,
      href,
      "aria-label": `${text}: ${g.title} (opens the Banger app)`,
      onclick: (e: Event) => {
        e.preventDefault();
        launch(g.id, { link: href, onMissing: () => navigate(downloadPath(g.id, action)) });
      },
    },
    text,
  );
}

/**
 * The Play button: a real thrdm:// link (works with JS off), upgraded to fall back to the download page. A coming-soon
 * game has nothing to play yet, so it gets its expected date and "Notify me in Banger" instead, everywhere.
 */
export function playButton(g: Game, size: "big" | "small" = "big"): HTMLElement {
  if (isComingSoon(g))
    return h(
      "span",
      { class: "coming-soon" },
      h("span", { class: "badge warn" }, comingSoonText(g)),
      actionButton(g, "notify", "Notify me in Banger", `btn${size === "small" ? " small" : ""}`),
    );
  return h(
    "a",
    {
      class: `btn-play ${size}`,
      href: playLink(g.id),
      "aria-label": `Play ${g.title} in the Banger app`,
      onclick: (e: Event) => {
        e.preventDefault();
        launch(g.id, { onMissing: () => navigate(downloadPath(g.id)) });
      },
    },
    h("span", { "aria-hidden": "true", class: "tri" }, "▶"),
    "Play",
  );
}
